import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Garden } from '@/types';
import { AlertTriangle } from 'lucide-react';

interface GardenDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  garden: Garden | null;
  onConfirm: (gardenId: string) => void;
  isLoading?: boolean;
}

export function GardenDeleteDialog({
  open,
  onOpenChange,
  garden,
  onConfirm,
  isLoading = false,
}: GardenDeleteDialogProps) {
  if (!garden) return null;

  const hasDevices =
    garden.controllers.length > 0 || garden.sensors.length > 0;
  const hasSchedules = garden.schedules.length > 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Eliminar jardín
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <p>
              ¿Estás seguro de que deseas eliminar el jardín{' '}
              <strong className="text-foreground">{garden.name}</strong>?
            </p>
            
            {(hasDevices || hasSchedules) && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm space-y-2">
                <p className="font-medium text-destructive">
                  Esta acción también eliminará:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1">
                  {garden.controllers.length > 0 && (
                    <li>{garden.controllers.length} controladora(s) asociada(s)</li>
                  )}
                  {garden.sensors.length > 0 && (
                    <li>{garden.sensors.length} sensor(es) asociado(s)</li>
                  )}
                  {garden.schedules.length > 0 && (
                    <li>{garden.schedules.length} programación(es)</li>
                  )}
                  {garden.triggers.length > 0 && (
                    <li>{garden.triggers.length} trigger(s)</li>
                  )}
                </ul>
              </div>
            )}
            
            <p className="text-destructive font-medium">
              Esta acción no se puede deshacer.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm(garden.id)}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isLoading}
          >
            {isLoading ? 'Eliminando...' : 'Eliminar jardín'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
